// Normaliza um documento OpenAPI 3.0/3.1 no modelo que a UI da referência
// consome. Tudo que a UI precisa sai daqui: nenhum componente de tela mexe em
// $ref, em `content["application/json"]` ou em schema.
//
// Extensões reconhecidas (opcionais — o spec continua válido sem elas):
//   x-codeSamples   [{ lang, label, source }]  snippets escritos à mão
//   x-rateLimit     string                     "60 req/min por credencial"
//   x-restricted    string                     rótulo de endpoint restrito
//   x-bestPractice  string                     bloco "Boa prática"
//   x-statusNotes   [{ code, label, type }]    type: ok | warn | err

import { operationSlug, slugify } from "../lib/slug.ts";
import {
  type Body,
  type Field,
  type FieldsOf,
  type Group,
  type MediaTypeObject,
  type NormalizedSpec,
  type OpenApiDocument,
  type Operation,
  type OperationObject,
  type Param,
  type PathItemObject,
  type ResponseInfo,
  type SchemaObject,
} from "./types.ts";

const METHODS = ["get", "post", "put", "patch", "delete", "head", "options"] as const;

// ── $ref ─────────────────────────────────────────────────────────────────────

function resolveRef(doc: OpenApiDocument, ref: unknown): unknown {
  if (typeof ref !== "string" || !ref.startsWith("#/")) return null;
  let node: unknown = doc;
  for (const rawPart of ref.slice(2).split("/")) {
    const part = rawPart.replace(/~1/g, "/").replace(/~0/g, "~");
    if (node == null || typeof node !== "object") return null;
    node = (node as Record<string, unknown>)[part];
  }
  return node ?? null;
}

// Resolve $ref uma camada por vez, sem descer na árvore inteira. Specs reais
// têm schemas recursivos (uma reserva contém insumos que contêm reservas) e uma
// resolução ansiosa e profunda entra em laço infinito.
function deref<T>(node: T | undefined | null, doc: OpenApiDocument): T | null {
  let current = node as Record<string, unknown> | null | undefined;
  let hops = 0;
  while (current && typeof current === "object" && current.$ref && hops < 32) {
    const next = resolveRef(doc, current.$ref) as Record<string, unknown> | null;
    if (!next) return current as T;
    // Um $ref pode vir ao lado de outras chaves (allOf com override).
    const { $ref, ...rest } = current;
    current = Object.keys(rest).length ? { ...next, ...rest } : next;
    hops += 1;
  }
  return (current ?? null) as T | null;
}

// ── Schema → rótulo de tipo e exemplo ────────────────────────────────────────

function typeLabel(schema: SchemaObject | undefined | null, doc: OpenApiDocument): string {
  const s = deref(schema, doc);
  if (!s) return "any";
  if (s.enum) return s.enum.map((v) => JSON.stringify(v)).join(" | ");

  // 3.1 permite type como array: ["string", "null"].
  const rawType = Array.isArray(s.type) ? s.type.filter((t) => t !== "null")[0] : s.type;

  if (rawType === "array") return `${typeLabel(s.items, doc)}[]`;
  if (s.format && (rawType === "string" || rawType === "integer" || rawType === "number")) {
    return s.format;
  }
  if (rawType) return rawType;
  const variantes = s.oneOf ?? s.anyOf;
  if (variantes) return variantes.map((x) => typeLabel(x, doc)).join(" | ");
  if (s.allOf) return "object";
  if (s.properties) return "object";
  return "any";
}

const FORMAT_SAMPLES: Record<string, string> = {
  date: "2026-04-01",
  "date-time": "2026-04-01T00:00:00Z",
  email: "contato@exemplo.com.br",
  uuid: "3f8a1c22-9d1e-4b7a-8f2e-1a0b9c8d7e6f",
  uri: "https://exemplo.com.br",
  password: "••••••",
  binary: "<binary>",
};

// Constrói um exemplo a partir do schema quando o spec não traz um `example`.
// `depth` corta recursão; `seen` corta ciclos que passam pelo mesmo schema.
function exampleFromSchema(
  schema: SchemaObject | undefined | null,
  doc: OpenApiDocument,
  depth = 0,
  seen: Set<SchemaObject> = new Set()
): unknown {
  const s = deref(schema, doc);
  if (!s || depth > 6) return null;

  if (s.example !== undefined) return s.example;
  if (s.default !== undefined) return s.default;
  if (Array.isArray(s.examples) && s.examples.length) return s.examples[0];
  if (s.enum?.length) return s.enum[0];
  if (s.const !== undefined) return s.const;

  if (s.allOf) {
    return s.allOf.reduce((acc, part) => Object.assign(acc, exampleFromSchema(part, doc, depth + 1, seen) || {}), {});
  }
  const variant = s.oneOf?.[0] || s.anyOf?.[0];
  if (variant) return exampleFromSchema(variant, doc, depth + 1, seen);

  const rawType = Array.isArray(s.type) ? s.type.filter((t) => t !== "null")[0] : s.type;

  if (rawType === "array") return [exampleFromSchema(s.items, doc, depth + 1, seen)].filter((v) => v !== null);

  if (rawType === "object" || s.properties) {
    if (seen.has(s)) return {};
    const nextSeen = new Set(seen).add(s);
    const out: Record<string, unknown> = {};
    for (const [key, prop] of Object.entries(s.properties || {})) {
      const value = exampleFromSchema(prop, doc, depth + 1, nextSeen);
      out[key] = value === null ? null : value;
    }
    if (!Object.keys(out).length && s.additionalProperties) return { chave: "valor" };
    return out;
  }

  if (rawType === "string") return (s.format ? FORMAT_SAMPLES[s.format] : undefined) ?? "string";
  if (rawType === "integer") return 0;
  if (rawType === "number") return 0.0;
  if (rawType === "boolean") return true;
  return null;
}

// Um exemplo que já veio como string é texto pronto (payload comentado, corpo
// não-JSON, resposta com "…" abreviando um array). Passar por JSON.stringify
// devolveria a mesma string entre aspas e com \n literais.
const pretty = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
};

// ── Corpo e respostas ────────────────────────────────────────────────────────

// Prefere JSON quando existe; senão pega o primeiro media type declarado.
function pickMedia(
  content: Record<string, MediaTypeObject> | undefined,
  doc: OpenApiDocument
): { contentType: string; media: MediaTypeObject | null } | null {
  if (!content) return null;
  const key = Object.keys(content).find((k) => k.includes("json")) || Object.keys(content)[0];
  if (!key) return null;
  const media = deref(content[key], doc);
  return { contentType: key, media };
}

function mediaExample(media: MediaTypeObject | null, doc: OpenApiDocument): unknown {
  if (!media) return null;
  if (media.example !== undefined) return media.example;
  const named = media.examples && Object.values(media.examples)[0];
  if (named) {
    const resolved = deref(named, doc);
    if (resolved && resolved.value !== undefined) return resolved.value;
  }
  return exampleFromSchema(media.schema, doc);
}

function parseBody(operation: OperationObject, doc: OpenApiDocument): Body | null {
  const requestBody = deref(operation.requestBody, doc);
  if (!requestBody) return null;
  const picked = pickMedia(requestBody.content, doc);
  if (!picked) return null;
  return {
    required: requestBody.required === true,
    description: requestBody.description || "",
    contentType: picked.contentType,
    example: pretty(mediaExample(picked.media, doc)),
    // O schema cru acompanha o exemplo: o exemplo mostra a forma, o schema é o
    // que carrega tipo, obrigatoriedade, enum e descrição de cada campo.
    schema: picked.media?.schema || null,
  };
}

// ── Campos de um schema, um nível por vez ────────────────────────────────────

// Devolve os campos do nível atual, marcando os que podem ser expandidos.
//
// Um nível por chamada, de propósito: a cadeia de BookingRQ desce por dezenas de
// schemas com recursão, e montar a árvore inteira de antemão geraria megabytes
// para uma tela onde o leitor abre dois ou três nós.
function makeFieldsOf(doc: OpenApiDocument): FieldsOf {
  return function fieldsOf(schemaOrRef: SchemaObject | null | undefined): Field[] {
    const schema = deref(schemaOrRef, doc);
    if (!schema) return [];

    // allOf compõe: junta as propriedades de todas as partes.
    const parts = schema.allOf ? schema.allOf.map((s) => deref(s, doc)) : [schema];
    const properties: Record<string, SchemaObject> = {};
    const required = new Set<string>();
    for (const part of parts) {
      Object.assign(properties, part?.properties || {});
      for (const name of part?.required || []) required.add(name);
    }

    // Array na raiz: descreve os campos do item, não do array.
    if (!Object.keys(properties).length) {
      const rawType = Array.isArray(schema.type) ? schema.type[0] : schema.type;
      if (rawType === "array" && schema.items) return fieldsOf(schema.items);
      return [];
    }

    return Object.entries(properties).map(([name, raw]): Field => {
      const prop = deref(raw, doc);
      const rawType = Array.isArray(prop?.type) ? prop.type.filter((t) => t !== "null")[0] : prop?.type;
      const isArray = rawType === "array";
      const inner = isArray ? deref(prop?.items, doc) : prop;
      // Expansível só quando há subcampos de fato — senão o chevron abre nada.
      const expandable = !!(inner && (inner.properties || inner.allOf));

      return {
        name,
        type: typeLabel(prop, doc),
        required: required.has(name),
        description: prop?.description || "",
        enum: prop?.enum || inner?.enum || null,
        example: prop?.example,
        deprecated: prop?.deprecated === true,
        // O nó filho é o schema do item quando for array.
        child: expandable ? ((isArray ? prop?.items : raw) ?? null) : null,
      };
    });
  };
}

function parseResponses(operation: OperationObject, doc: OpenApiDocument): ResponseInfo[] {
  const responses = operation.responses || {};
  return (
    Object.entries(responses)
      .map(([status, raw]) => {
        const response = deref(raw, doc);
        const picked = pickMedia(response?.content, doc);
        return {
          status,
          description: response?.description || "",
          contentType: picked?.contentType || null,
          example: picked ? pretty(mediaExample(picked.media, doc)) : null,
          schema: picked?.media?.schema || null,
        };
      })
      // 2xx primeiro, depois erros em ordem crescente. "default" por último.
      .sort((a, b) => {
        const rank = (s: string) => (s === "default" ? 999 : Number(s) || 500);
        return rank(a.status) - rank(b.status);
      })
  );
}

function parseParams(operation: OperationObject, pathItem: PathItemObject, doc: OpenApiDocument): Param[] {
  // Parâmetros declarados no path item valem para todos os verbos daquele path,
  // e o verbo pode sobrescrever um deles (mesmo name + in).
  const merged = new Map();
  for (const raw of [...(pathItem.parameters || []), ...(operation.parameters || [])]) {
    const p = deref(raw, doc);
    if (!p?.name) continue;
    merged.set(`${p.in}:${p.name}`, p);
  }

  return [...merged.values()].map((p) => ({
    name: p.name,
    loc: p.in,
    type: typeLabel(p.schema, doc),
    required: p.required === true || p.in === "path",
    deprecated: p.deprecated === true,
    desc: p.description || "",
    example: p.example ?? (p.schema ? exampleFromSchema(p.schema, doc) : undefined),
  }));
}

// ── Normalização ─────────────────────────────────────────────────────────────

// Sem `tags`, agrupa pelo primeiro segmento do path — melhor do que jogar 100
// endpoints numa única lista quando o spec do backend não usa tags.
function fallbackTag(path: string): string {
  const seg = String(path).split("/").filter(Boolean)[0] || "geral";
  return seg.charAt(0).toUpperCase() + seg.slice(1);
}

export function normalizeSpec(
  doc: OpenApiDocument | null | undefined,
  { tagOrder = [] }: { tagOrder?: string[] } = {}
): NormalizedSpec {
  if (!doc || typeof doc !== "object" || !doc.paths) {
    throw new Error("Documento OpenAPI inválido: não encontrei `paths`.");
  }

  // A URL base é mantida como TEMPLATE, com os {placeholders} visíveis.
  //
  // Expandir as variáveis pelos `default` produziria uma URL que parece real e não
  // é: quando cada cliente tem o seu próprio domínio, "https://seu-dominio.com.br"
  // seria copiado como se funcionasse. Deixar `{domain}` à vista comunica que há
  // algo a substituir, e as descrições das variáveis dizem o quê.
  const server = doc.servers?.[0] || null;
  const baseUrl = server?.url || "";
  const baseUrlVars = Object.entries(server?.variables || {}).map(([name, v]) => ({
    name,
    default: v.default ?? "",
    enum: v.enum || null,
    description: v.description || "",
  }));
  const byTag = new Map();

  for (const [path, rawPathItem] of Object.entries(doc.paths)) {
    const pathItem = deref(rawPathItem, doc);
    if (!pathItem) continue;

    for (const method of METHODS) {
      const operation = pathItem[method];
      if (!operation) continue;

      const tag = operation.tags?.[0] || fallbackTag(path);
      const upper = method.toUpperCase();

      const entry = {
        id: operationSlug(upper, path),
        method: upper,
        path,
        title: operation.summary || `${upper} ${path}`,
        desc: operation.description || "",
        deprecated: operation.deprecated === true,
        operationId: operation.operationId || null,
        params: parseParams(operation, pathItem, doc),
        body: parseBody(operation, doc),
        responses: parseResponses(operation, doc),
        // Vendor extensions — a documentação editorial que o spec do backend
        // normalmente não carrega.
        codeSamples: operation["x-codeSamples"] || operation["x-code-samples"] || null,
        rateLimit: operation["x-rateLimit"] || null,
        restricted: operation["x-restricted"] || null,
        bestPractice: operation["x-bestPractice"] || null,
        statusNotes: operation["x-statusNotes"] || null,
      };

      if (!byTag.has(tag)) byTag.set(tag, []);
      byTag.get(tag).push(entry);
    }
  }

  const tagMeta = new Map((doc.tags || []).map((t) => [t.name, t]));

  const groups = [...byTag.entries()].map(([name, operations]) => ({
    // A tag vira o último segmento da URL: /pt/api/disponibilidade
    id: slugify(name),
    name,
    description: tagMeta.get(name)?.description || "",
    operations,
  }));

  // Ordem declarada em docs.config.js primeiro; o resto em ordem alfabética, no
  // fim — assim um endpoint com tag nova aparece sozinho, sem precisar de
  // config, em vez de sumir da documentação.
  const rank = (name: string) => {
    const i = tagOrder.indexOf(name);
    return i === -1 ? tagOrder.length : i;
  };
  groups.sort((a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name));

  return {
    info: doc.info || {},
    baseUrl,
    baseUrlVars,
    serverDescription: server?.description || "",
    // Resolve os campos de um schema sob demanda, para a tabela expansível.
    fieldsOf: makeFieldsOf(doc),
    groups,
    // Índice plano para busca e resolução de rota.
    operations: groups.flatMap((g: Group) =>
      g.operations.map((op: Operation) => ({ ...op, tag: g.name, tagSlug: g.id }))
    ),
  };
}

export default normalizeSpec;
